import { Injectable } from '@angular/core';
import { AverageGraph, MonthlyMoodActivityData } from '../models/studentProfileModels';

/** Chart.js data shape for the Feedback Rating Over Time bar chart. */
export type FeedbackRatingMandEChartData = {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string[];
    borderColor: string[];
    borderWidth: number;
    borderRadius?:
      | number
      | { topLeft?: number; topRight?: number; bottomLeft?: number; bottomRight?: number };
    borderSkipped?: boolean;
  }[];
};

/** Chart.js data shape for the Monthly Mood Logging Activity bar chart. */
export type MonthlyMoodActivityChartData = FeedbackRatingMandEChartData;

@Injectable({
  providedIn: 'root',
})
export class FeedbackRatingChartService {
  readonly mAndEChartOptions: any = {
    elements: {
      bar: {
        borderRadius: { topLeft: 8, topRight: 8, bottomLeft: 0, bottomRight: 0 },
        borderSkipped: false,
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        filter: (item: any) => {
          const y = item.parsed?.y;
          return y != null && Number(y) > 0;
        },
        callbacks: {
          label: (ctx: any) => {
            const y = ctx.parsed?.y;
            if (y == null) return '';
            const n = Number(y);
            const text = Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
            return `Rating: ${text}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 45,
          minRotation: 0,
          color: (ctx: any) => {
            const chart = ctx.chart;
            const data = chart?.data?.datasets?.[0]?.data as number[] | undefined;
            const tickVal = ctx.tick?.value;
            const i = typeof tickVal === 'number' ? tickVal : ctx.index;
            const v = data != null && i != null && i >= 0 && i < data.length ? Number(data[i]) : 0;
            return v > 0 ? 'rgba(30, 38, 71, 1)' : 'rgba(179, 193, 221, 1)';
          },
        },
      },
      y: { beginAtZero: true, max: 5, ticks: { stepSize: 1 } },
    },
  };

  /** Full Jan–Dec axis for one calendar year (short names, e.g. Jan, Feb, …). */
  getFeedbackRatingMonthLabelsForCalendarYear(year: number): string[] {
    const labels: string[] = [];
    for (let m = 0; m < 12; m++) {
      labels.push(new Date(year, m, 1).toLocaleString('en-US', { month: 'short' }));
    }
    return labels;
  }

  /** Short month labels (e.g. Jan) when range is one calendar year; else "Jan 2025" for disambiguation. */
  getFeedbackRatingMonthLabelsInRange(start: Date, end: Date): string[] {
    const startMonth = new Date(start.getFullYear(), start.getMonth(), 1);
    const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
    if (startMonth > endMonth) return [];

    const sameCalendarYear = startMonth.getFullYear() === endMonth.getFullYear();
    const labels: string[] = [];
    for (let d = new Date(startMonth); d <= endMonth; d.setMonth(d.getMonth() + 1)) {
      const short = d.toLocaleString('en-US', { month: 'short' });
      labels.push(sameCalendarYear ? short : `${short} ${d.getFullYear()}`);
    }
    return labels;
  }

  /**
   * Feedback Rating Over Time: always renders a full 12-month calendar axis
   * (Jan–Dec) anchored on the end date's year (falling back to the start
   * date's year, then the current year). API values are slotted into the
   * matching month; any missing months render as zero-value, grey bars so the
   * full 12-month axis is visible even in a zero state or when the API only
   * returns a partial set of months.
   */
  buildFeedbackRatingOverTimeChartData(
    averages: AverageGraph[] | null | undefined,
    start: Date,
    end: Date
  ): FeedbackRatingMandEChartData {
    const flat = (averages ?? []).flat().filter(Boolean) as AverageGraph[];
    const anchorYear = this.resolveAnchorYear(start, end);

    const labels: string[] = [];
    const dataValues: number[] = [];

    for (let m = 0; m < 12; m++) {
      labels.push(
        new Date(anchorYear, m, 1).toLocaleString('en-US', { month: 'short' })
      );

      let v = 0;
      for (const item of flat) {
        const ym = this.parseFeedbackMonthToYearMonth(
          String(item.month ?? ''),
          anchorYear
        );
        if (ym && ym.year === anchorYear && ym.month === m) {
          v = Number(item.averageScore) || 0;
          break;
        }
      }
      dataValues.push(v);
    }

    const normal = 'rgba(52, 71, 103, 1)';
    const empty = 'rgba(179, 193, 221, 1)';
    const backgroundColor = dataValues.map((r) => (r > 0 ? normal : empty));
    const borderColor = [...backgroundColor];

    return {
      labels,
      datasets: [
        {
          label: 'Rating',
          data: dataValues,
          backgroundColor,
          borderWidth: 1,
          borderColor,
          borderRadius: { topLeft: 8, topRight: 8, bottomLeft: 0, bottomRight: 0 },
          borderSkipped: false,
        },
      ],
    };
  }

  /**
   * Chart.js options for the Monthly Mood Logging Activity bar chart. Mirrors
   * the Feedback Rating Over Time options, but auto-scales the Y axis (no
   * fixed max) and surfaces raw counts in the tooltip.
   */
  readonly monthlyMoodActivityChartOptions: any = {
    elements: {
      bar: {
        borderRadius: { topLeft: 8, topRight: 8, bottomLeft: 0, bottomRight: 0 },
        borderSkipped: false,
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        filter: (item: any) => {
          const y = item.parsed?.y;
          return y != null && Number(y) > 0;
        },
        callbacks: {
          label: (ctx: any) => {
            const y = ctx.parsed?.y;
            if (y == null) return '';
            const n = Number(y);
            return `Users: ${n.toLocaleString('en-US')}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          color: (ctx: any) => {
            const chart = ctx.chart;
            const data = chart?.data?.datasets?.[0]?.data as number[] | undefined;
            const tickVal = ctx.tick?.value;
            const i = typeof tickVal === 'number' ? tickVal : ctx.index;
            const v = data != null && i != null && i >= 0 && i < data.length ? Number(data[i]) : 0;
            return v > 0 ? 'rgba(30, 38, 71, 1)' : 'rgba(179, 193, 221, 1)';
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(239, 239, 239, 1)', borderDash: [4, 4] },
        ticks: {
          callback: (value: any) => Number(value).toLocaleString('en-US'),
        },
      },
    },
  };

  /**
   * Monthly Mood Logging Activity: always renders a full 12-month calendar
   * axis (Jan–Dec) anchored on the end date's year (falling back to the start
   * date's year, then the current year). API values are slotted into the
   * matching month; any missing months render as zero-value, grey bars so the
   * full 12-month axis is always visible — even in a zero state or when the
   * API only returns a partial set of months.
   */
  buildMonthlyMoodActivityChartData(
    activity: MonthlyMoodActivityData[] | null | undefined,
    start: Date,
    end: Date
  ): MonthlyMoodActivityChartData {
    const flat = (activity ?? []).filter(Boolean) as MonthlyMoodActivityData[];
    const anchorYear = this.resolveAnchorYear(start, end);

    const labels: string[] = [];
    const dataValues: number[] = [];

    for (let m = 0; m < 12; m++) {
      labels.push(
        new Date(anchorYear, m, 1).toLocaleString('en-US', { month: 'short' })
      );

      let v = 0;
      for (const item of flat) {
        const ym = this.parseFeedbackMonthToYearMonth(
          String(item.log_month ?? ''),
          anchorYear
        );
        if (ym && ym.year === anchorYear && ym.month === m) {
          v = Number(item.unique_learners) || 0;
          break;
        }
      }
      dataValues.push(v);
    }

    const normal = 'rgba(30, 38, 71, 1)';
    const empty = 'rgba(220, 226, 240, 1)';
    const backgroundColor = dataValues.map((r) => (r > 0 ? normal : empty));
    const borderColor = [...backgroundColor];

    return {
      labels,
      datasets: [
        {
          label: 'Mood Loggers',
          data: dataValues,
          backgroundColor,
          borderWidth: 1,
          borderColor,
          borderRadius: { topLeft: 8, topRight: 8, bottomLeft: 0, bottomRight: 0 },
          borderSkipped: false,
        },
      ],
    };
  }

  private resolveAnchorYear(start: Date, end: Date): number {
    const isValid = (d: Date | null | undefined): d is Date =>
      d instanceof Date && !Number.isNaN(d.getTime());
    if (isValid(end)) return end.getFullYear();
    if (isValid(start)) return start.getFullYear();
    return new Date().getFullYear();
  }

  private monthNameToIndex(name: string): number | null {
    const parsed = Date.parse(`${name} 1, 2000`);
    if (!Number.isNaN(parsed)) {
      return new Date(parsed).getMonth();
    }
    const short = name.slice(0, 3).toLowerCase();
    const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    const idx = months.indexOf(short);
    return idx >= 0 ? idx : null;
  }

  private parseFeedbackMonthToYearMonth(
    monthStr: string,
    fallbackYear: number
  ): { year: number; month: number } | null {
    const t = monthStr.trim();
    if (!t) return null;

    const iso = t.match(/^(\d{4})-(\d{2})(?:-(\d{2}))?/);
    if (iso) {
      return { year: Number(iso[1]), month: Number(iso[2]) - 1 };
    }

    const withYear = t.match(/^([a-z]+)\s+(\d{4})$/i);
    if (withYear) {
      const m = this.monthNameToIndex(withYear[1]);
      if (m != null) {
        return { year: Number(withYear[2]), month: m };
      }
    }

    const parsed = Date.parse(t);
    if (!Number.isNaN(parsed)) {
      const dt = new Date(parsed);
      return { year: dt.getFullYear(), month: dt.getMonth() };
    }

    const word = t.match(/^([a-z]+)/i);
    if (word) {
      const m = this.monthNameToIndex(word[1]);
      if (m != null) {
        return { year: fallbackYear, month: m };
      }
    }

    return null;
  }
}
